import './CreateDialog.scss';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';

import useAxiosHook from '../../../../hooks/useAxiosHook';
import useMediaQuery from '../../../../hooks/useMediaQuery';
import useToastMessage from '../../../../hooks/useToastMessage';
import { WithData } from '../../../../types/api';
import { sequential } from '../../../../utils/helpers/functions';
import DialogSpinner from '../../../Dialogs/DialogSpinner/DialogSpinner';
import { HolidayType } from '../Holiday.functions';
import { CreateHoliday } from './CreateDialog.functions';
import FormContainer from './FormContainer';

type Props = {
  isShown: boolean;
  idHoliday: number | undefined;
  isEditDialog: boolean;
  onHide: () => void;
  reloadCollection: () => void;
};

function CreateDialog({
  isShown,
  idHoliday,
  isEditDialog,
  onHide,
  reloadCollection,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const isOnMobile = useMediaQuery('(max-width: 768px)');

  const { data: holidayData, isLoading: isHolidayDataLoading } = useAxiosHook<
    WithData<HolidayType>
  >(`/holidays/${idHoliday}`, {
    skipWhen: !isEditDialog || !idHoliday,
  });

  const {
    data: createData,
    error: createError,
    isLoading: isCreateLoading,
    reload: createReload,
  } = useAxiosHook(
    {
      url: '/holidays',
      method: 'POST',
    },
    {
      skipWhen: true,
    }
  );

  useToastMessage(createData, createError, {
    success: {
      summary: t('The holiday has been created successfully.'),
      callback: () => {
        sequential(onHide, reloadCollection);
        console.log('createData ', createData);
      },
    },
    error: {
      summary: t('An error occurred while creating the holiday.'),
      callback: () => {
        console.error('createError ', createError);
        if (createError?.response?.status === 409) {
          return t('The holiday already exists.');
        }
        return t('An error occurred while creating the holiday.');
      },
    },
  });

  const {
    data: editData,
    error: editError,
    isLoading: isEditLoading,
    reload: editReload,
  } = useAxiosHook(
    {
      url: `/holidays/${idHoliday}`,
      method: 'PUT',
    },
    {
      skipWhen: true,
    }
  );

  const {
    data: deleteData,
    error: deleteError,
    isLoading: isDeleteLoading,
    reload: deleteReload,
  } = useAxiosHook(
    {
      url: `/holidays/${idHoliday}`,
      method: 'DELETE',
    },
    {
      skipWhen: true,
    }
  );

  useToastMessage(editData, editError, {
    success: {
      summary: t('The holiday has been edited successfully.'),
      callback: () => sequential(onHide, reloadCollection),
    },
    error: {
      summary: t('An error occurred while editing the holiday.'),
      callback: onHide,
    },
  });

  useToastMessage(deleteData, deleteError, {
    success: {
      summary: t('The holiday has been deleted successfully.'),
      callback: () => sequential(onHide, reloadCollection),
    },
    error: {
      summary: t('An error occurred while deleting the holiday.'),
      callback: onHide,
    },
  });

  function onDelete() {
    deleteReload();
  }

  const dialogFooter = isHolidayDataLoading ? null : (
    <div className="dialog-footer">
      <div>
        <Button
          type="button"
          label={t('Delete')}
          className="p-button-danger"
          onClick={() => onDelete()}
        />
      </div>
      <div>
        <Button
          type="button"
          label={t('Cancel')}
          className="p-button-text"
          onClick={() => onHide()}
        />
        <Button
          type="submit"
          form="create-edit-holiday"
          label={
            isDeleteLoading
              ? t('Deleting...')
              : isCreateLoading || isEditLoading
              ? t('Saving...')
              : isEditDialog
              ? t('Save changes')
              : t('Add holiday')
          }
          disabled={isCreateLoading || isEditLoading || isDeleteLoading}
        />
      </div>
    </div>
  );

  function handleCreation(data: CreateHoliday) {
    createReload({ data });
  }

  function handleEditing(data: CreateHoliday) {
    editReload({ data });
  }

  return (
    <Dialog
      header={isEditDialog ? t('Edit holiday') : t('Create holiday')}
      footer={dialogFooter}
      visible={isShown}
      resizable={false}
      onHide={onHide}
      maximizable
      maximized={isOnMobile}
      style={{ width: 680, maxWidth: '100%' }}
      className="create-holiday-page-dialog"
    >
      {isHolidayDataLoading ? (
        <DialogSpinner />
      ) : (
        <FormContainer
          isEditDialog={isEditDialog}
          holidayData={holidayData?.data}
          handleCreation={handleCreation}
          handleEditing={handleEditing}
        />
      )}
    </Dialog>
  );
}

export default CreateDialog;
